/**
 * Data Flow Monitor — where data leaves the ArgusAI platform.
 *
 * The key question: "Is our AI sending sensitive data to external tools?"
 *
 * Visualization approach (hand-rolled, no libraries): a 3-column Sankey-style
 * layout — Sources (left) → MCP Gateway (middle) → Destinations (right).
 * Ribbon thickness = bytesOut24h; color = classification of data flowing.
 *
 * Below the viz: Flows table sortable by anomaly score (highest first) with
 * approved/unapproved flag. Unapproved flows or high anomaly scores get a
 * red-tinted row.
 *
 * Drill-in: clicking a flow row opens a sheet showing sample events on that
 * flow, bytes over time chart (mocked), and approve/block actions.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Network, Search, Download, ShieldAlert, AlertTriangle,
  ExternalLink, Bot, Workflow as WorkflowIcon, Server, Globe,
  Sparkles, ChevronRight, CheckCircle2, XCircle, ArrowRight,
  Activity, Lock, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  CLASSIFICATION_STYLES,
  type DataFlowSummary, type ActivityEvent, type Classification,
} from '@/lib/mock-trust-activity';
import { useTrustDataFlows, useTrustActivity } from '@/lib/use-trust-module';
import { ActivityDetailSheet } from '@/components/trust/activity-detail-sheet';
import { triggerDownload, generateCsv } from '@/lib/trust-downloads';

export const Route = createFileRoute('/trust/data-flows')({
  component: DataFlowMonitorPage,
});

const TIER_META: Record<DataFlowSummary['destination']['kind'], { label: string; color: string; ringColor: string }> = {
  internal:     { label: 'Internal',      color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',          ringColor: 'ring-zinc-400/40' },
  tier_1_iq:    { label: 'Tier 1 (IQ)',   color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', ringColor: 'ring-emerald-400/50' },
  tier_2_ext:   { label: 'Tier 2 (Ext)',  color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',      ringColor: 'ring-amber-400/50' },
  tier_3_forge: { label: 'Tier 3 (Forge)',color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',  ringColor: 'ring-orange-400/50' },
  public_web:   { label: 'Public Web',    color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',              ringColor: 'ring-red-400/60' },
};

function DataFlowMonitorPage() {
  // Real-data: derived flows from audit_logs externalDestination metadata
  const { data: realFlows = [] } = useTrustDataFlows();
  const { data: realEvents = [] } = useTrustActivity(500);
  const DATA_FLOWS = realFlows;
  const ACTIVITY_EVENTS = realEvents;
  void DATA_FLOWS; void ACTIVITY_EVENTS;
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<'all' | DataFlowSummary['destination']['kind']>('all');
  const [classFilter, setClassFilter] = useState<'all' | Classification>('all');
  const [approvedFilter, setApprovedFilter] = useState<'all' | 'approved' | 'unapproved'>('all');
  const [selectedFlow, setSelectedFlow] = useState<DataFlowSummary | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(null);

  const filtered = useMemo(() => {
    return DATA_FLOWS.filter(f => {
      if (tierFilter !== 'all' && f.destination.kind !== tierFilter) return false;
      if (classFilter !== 'all' && !f.classifications.includes(classFilter)) return false;
      if (approvedFilter === 'approved' && !f.approved) return false;
      if (approvedFilter === 'unapproved' && f.approved) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        f.source.label.toLowerCase().includes(q)
        || f.destination.label.toLowerCase().includes(q)
        || f.destination.host.toLowerCase().includes(q)
      );
    });
  }, [DATA_FLOWS, search, tierFilter, classFilter, approvedFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'anomalyScore', 'desc');

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalEvents24h = DATA_FLOWS.reduce((s, f) => s + f.eventCount24h, 0);
    const totalBytesOut = DATA_FLOWS.reduce((s, f) => s + f.bytesOut24h, 0);
    const totalBytesIn = DATA_FLOWS.reduce((s, f) => s + f.bytesIn24h, 0);
    const unapproved = DATA_FLOWS.filter(f => !f.approved).length;
    const highAnomaly = DATA_FLOWS.filter(f => f.anomalyScore >= 50).length;
    const sensitive = DATA_FLOWS.filter(f => f.classifications.some(c => c === 'itar' || c === 'cui' || c === 'phi')).length;
    return { totalEvents24h, totalBytesOut, totalBytesIn, unapproved, highAnomaly, sensitive };
  }, []);

  // ── Sankey-style viz data ────────────────────────────────────────────────
  const sourcesGrouped = useMemo(() => {
    const m = new Map<string, { id: string; label: string; kind: DataFlowSummary['source']['kind']; totalBytes: number }>();
    filtered.forEach(f => {
      const cur = m.get(f.source.id) ?? { id: f.source.id, label: f.source.label, kind: f.source.kind, totalBytes: 0 };
      cur.totalBytes += f.bytesOut24h;
      m.set(f.source.id, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.totalBytes - a.totalBytes);
  }, [filtered]);

  const destinationsGrouped = useMemo(() => {
    const m = new Map<string, { id: string; label: string; host: string; kind: DataFlowSummary['destination']['kind']; totalBytes: number; flows: number }>();
    filtered.forEach(f => {
      const cur = m.get(f.destination.host) ?? { id: f.destination.host, label: f.destination.label, host: f.destination.host, kind: f.destination.kind, totalBytes: 0, flows: 0 };
      cur.totalBytes += f.bytesOut24h + f.bytesIn24h;
      cur.flows++;
      m.set(f.destination.host, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.totalBytes - a.totalBytes);
  }, [filtered]);

  // ── Export ───────────────────────────────────────────────────────────────
  const handleExport = () => {
    const csv = generateCsv(
      ['Source', 'Destination', 'Host', 'Tier', 'Classifications', 'Events (24h)', 'Bytes Out (24h)', 'Bytes In (24h)', 'Last Activity', 'Approved', 'Anomaly Score', 'First Seen'],
      sorted.map(f => [
        f.source.label,
        f.destination.label,
        f.destination.host,
        f.destination.kind,
        f.classifications.join(' | '),
        f.eventCount24h,
        f.bytesOut24h,
        f.bytesIn24h,
        f.lastActivityAt,
        f.approved ? 'yes' : 'no',
        f.anomalyScore,
        f.firstSeen,
      ]),
    );
    triggerDownload({
      filename: `data-flows-${new Date().toISOString().split('T')[0]}.csv`,
      content: csv,
      mimeType: 'text/csv',
    });
    toast.success(`Exported ${sorted.length} flows`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Network className="h-6 w-6 text-primary" />
            Data Flow Monitor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Where data exits the AI platform. Every outbound request is tracked with classification, destination,
            tier, and bytes transferred. Unapproved flows and high-anomaly scores are flagged.
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={handleExport}>
          <Download className="h-3.5 w-3.5" /> Export ({sorted.length})
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Active Flows</span>
              <Network className="h-3.5 w-3.5 text-primary" />
            </div>
            <p className="text-2xl font-bold">{DATA_FLOWS.length}</p>
            <p className="text-[10px] text-muted-foreground">distinct src → dst</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Events (24h)</span>
              <Activity className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-2xl font-bold">{stats.totalEvents24h.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">data transfer events</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Bytes Out (24h)</span>
              <ArrowRight className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-2xl font-bold">{formatBytes(stats.totalBytesOut)}</p>
            <p className="text-[10px] text-muted-foreground">leaving platform</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Sensitive Flows</span>
              <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-2xl font-bold">{stats.sensitive}</p>
            <p className="text-[10px] text-muted-foreground">ITAR / CUI / PHI involved</p>
          </CardContent>
        </Card>
        <Card className={stats.unapproved > 0 ? 'ring-1 ring-red-500/30' : ''}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Unapproved</span>
              <ShieldAlert className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
            </div>
            <p className={cn('text-2xl font-bold', stats.unapproved > 0 && 'text-red-600 dark:text-red-400')}>
              {stats.unapproved}
            </p>
            <p className="text-[10px] text-muted-foreground">require review</p>
          </CardContent>
        </Card>
        <Card className={stats.highAnomaly > 0 ? 'ring-1 ring-orange-500/30' : ''}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">High Anomaly</span>
              <AlertTriangle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            </div>
            <p className={cn('text-2xl font-bold', stats.highAnomaly > 0 && 'text-orange-600 dark:text-orange-400')}>
              {stats.highAnomaly}
            </p>
            <p className="text-[10px] text-muted-foreground">anomaly score ≥ 50</p>
          </CardContent>
        </Card>
      </div>

      {/* Sankey-style 3-column viz */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold">Flow Visualization</h2>
            <p className="text-xs text-muted-foreground">Agents → Gateway → Destinations. Row color by destination tier.</p>
          </div>
        </div>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-[1fr_140px_1.2fr] gap-3 items-start">
              {/* Sources column */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 text-right">Sources</p>
                <div className="space-y-1.5">
                  {sourcesGrouped.map(s => (
                    <div key={s.id} className="rounded-md border bg-card px-3 py-2 flex items-center justify-end gap-2">
                      <div className="text-right min-w-0">
                        <p className="text-xs font-medium truncate">{s.label}</p>
                        <p className="text-[9px] text-muted-foreground">{formatBytes(s.totalBytes)} out</p>
                      </div>
                      {s.kind === 'agent' ? <Bot className="h-3.5 w-3.5 text-pink-600 dark:text-pink-400 shrink-0" /> :
                        s.kind === 'workflow' ? <WorkflowIcon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" /> :
                          <Sparkles className="h-3.5 w-3.5 text-zinc-600 dark:text-zinc-400 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Middle: Gateway */}
              <div className="flex flex-col items-center pt-6">
                <div className="rounded-lg border bg-gradient-to-b from-primary/10 to-primary/5 border-primary/30 p-4 text-center w-full">
                  <Server className="h-5 w-5 mx-auto mb-1 text-primary" />
                  <p className="text-xs font-semibold">MCP Gateway</p>
                  <p className="text-[9px] text-muted-foreground mt-0.5">
                    + Classification Gate
                  </p>
                  <p className="text-[9px] text-muted-foreground">
                    + Tool Sanitization
                  </p>
                </div>
                <div className="flex flex-col items-center gap-1 mt-3 text-[9px] text-muted-foreground">
                  <div className="h-px w-px" />
                  <ChevronRight className="h-3 w-3" />
                  <div className="h-px w-px" />
                </div>
              </div>

              {/* Destinations column */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Destinations</p>
                <div className="space-y-1.5">
                  {destinationsGrouped.map(d => {
                    const tier = TIER_META[d.kind];
                    const unapproved = DATA_FLOWS.some(f => f.destination.host === d.host && !f.approved);
                    return (
                      <div
                        key={d.id}
                        className={cn(
                          'rounded-md border bg-card px-3 py-2 flex items-center gap-2',
                          d.kind === 'public_web' && 'ring-1 ring-red-500/30',
                          unapproved && 'ring-1 ring-red-500/30',
                        )}
                      >
                        {d.kind === 'public_web' ? <Globe className="h-3.5 w-3.5 text-red-600 dark:text-red-400 shrink-0" /> :
                          d.kind === 'tier_2_ext' ? <ExternalLink className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" /> :
                            <Server className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium truncate">{d.label}</p>
                          <p className="text-[9px] text-muted-foreground truncate">
                            <code>{d.host}</code> · {d.flows} flow{d.flows === 1 ? '' : 's'} · {formatBytes(d.totalBytes)}
                          </p>
                        </div>
                        <Badge variant="outline" className={cn('text-[9px] shrink-0', tier.color)}>{tier.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search flows, destinations..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>
        <Select value={tierFilter} onValueChange={v => setTierFilter(v as typeof tierFilter)}>
          <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
            <SelectItem value="tier_1_iq">Tier 1 (IQ)</SelectItem>
            <SelectItem value="tier_2_ext">Tier 2 (Ext)</SelectItem>
            <SelectItem value="tier_3_forge">Tier 3 (Forge)</SelectItem>
            <SelectItem value="public_web">Public Web</SelectItem>
          </SelectContent>
        </Select>
        <Select value={classFilter} onValueChange={v => setClassFilter(v as typeof classFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Classification" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classifications</SelectItem>
            {(Object.keys(CLASSIFICATION_STYLES) as Classification[]).map(c => (
              <SelectItem key={c} value={c} className="text-xs">{CLASSIFICATION_STYLES[c].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={approvedFilter} onValueChange={v => setApprovedFilter(v as typeof approvedFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All flows</SelectItem>
            <SelectItem value="approved">Approved only</SelectItem>
            <SelectItem value="unapproved">Unapproved only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Flows table */}
      <div className="rounded-lg border overflow-x-auto">
        <div className="min-w-[1200px]">
          <div className="grid grid-cols-[minmax(220px,1.5fr)_minmax(220px,1.5fr)_110px_120px_110px_110px_90px_110px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider">
            <SortableHeader label="Source" sortKey="source.label" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Destination" sortKey="destination.host" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Tier" sortKey="destination.kind" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Classification" sortKey="classifications" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Events 24h" sortKey="eventCount24h" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Bytes 24h" sortKey="bytesOut24h" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Approved" sortKey="approved" currentSort={sortConfig} onSort={handleSort} />
            <SortableHeader label="Anomaly" sortKey="anomalyScore" currentSort={sortConfig} onSort={handleSort} />
          </div>
          <div className="divide-y">
            {sorted.map(f => (
              <FlowRow key={f.id} flow={f} onSelect={setSelectedFlow} />
            ))}
            {sorted.length === 0 && (
              <div className="p-10 text-center">
                <Network className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
                <p className="text-xs font-medium">No flows match your filters</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Flow detail */}
      <FlowDetailSheet
        flow={selectedFlow}
        onClose={() => setSelectedFlow(null)}
        onSelectEvent={(ev) => { setSelectedFlow(null); setSelectedEvent(ev); }}
        allEvents={ACTIVITY_EVENTS}
      />

      {/* Event detail */}
      <ActivityDetailSheet
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        allEvents={ACTIVITY_EVENTS}
      />
    </div>
  );
}

// ─── Flow Row ───────────────────────────────────────────────────────────────

function FlowRow({ flow, onSelect }: { flow: DataFlowSummary; onSelect: (f: DataFlowSummary) => void }) {
  const tier = TIER_META[flow.destination.kind];
  const anomalyColor =
    flow.anomalyScore >= 70 ? 'text-red-600 dark:text-red-400' :
    flow.anomalyScore >= 40 ? 'text-orange-600 dark:text-orange-400' :
    flow.anomalyScore >= 20 ? 'text-amber-600 dark:text-amber-400' :
    'text-muted-foreground';

  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(220px,1.5fr)_minmax(220px,1.5fr)_110px_120px_110px_110px_90px_110px] gap-3 px-4 py-3 items-center text-xs hover:bg-muted/30 cursor-pointer',
        !flow.approved && 'bg-red-500/[0.03]',
      )}
      onClick={() => onSelect(flow)}
    >
      {/* Source */}
      <div className="flex items-center gap-2 min-w-0">
        {flow.source.kind === 'agent' ? <Bot className="h-3.5 w-3.5 text-pink-600 dark:text-pink-400 shrink-0" /> :
          flow.source.kind === 'workflow' ? <WorkflowIcon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" /> :
            <Sparkles className="h-3.5 w-3.5 text-zinc-600 dark:text-zinc-400 shrink-0" />}
        <div className="min-w-0">
          <p className="font-medium truncate">{flow.source.label}</p>
          <p className="text-[9px] text-muted-foreground capitalize">{flow.source.kind}</p>
        </div>
      </div>

      {/* Destination */}
      <div className="flex items-center gap-2 min-w-0">
        {flow.destination.kind === 'public_web' ? <Globe className="h-3.5 w-3.5 text-red-600 dark:text-red-400 shrink-0" /> :
          flow.destination.kind === 'tier_2_ext' ? <ExternalLink className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" /> :
            <Server className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />}
        <div className="min-w-0">
          <p className="font-medium truncate">{flow.destination.label}</p>
          <p className="text-[9px] text-muted-foreground truncate font-mono">{flow.destination.host}</p>
        </div>
      </div>

      <Badge variant="outline" className={cn('text-[9px] w-fit', tier.color)}>{tier.label}</Badge>

      <div className="flex flex-wrap gap-1">
        {flow.classifications.map(c => (
          <Badge key={c} variant="outline" className={cn('text-[8px] h-4 px-1', CLASSIFICATION_STYLES[c].color)}>
            {CLASSIFICATION_STYLES[c].label}
          </Badge>
        ))}
      </div>

      <span className="font-medium">{flow.eventCount24h.toLocaleString()}</span>

      <div className="text-[11px]">
        <p>{formatBytes(flow.bytesOut24h)} <span className="text-muted-foreground">out</span></p>
        <p className="text-muted-foreground">{formatBytes(flow.bytesIn24h)} in</p>
      </div>

      {flow.approved ? (
        <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 w-fit">
          <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Yes
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20 w-fit">
          <XCircle className="h-2.5 w-2.5 mr-0.5" /> No
        </Badge>
      )}

      <div className="flex items-center gap-1.5">
        <span className={cn('font-semibold', anomalyColor)}>{flow.anomalyScore}</span>
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all',
              flow.anomalyScore >= 70 ? 'bg-red-500' :
                flow.anomalyScore >= 40 ? 'bg-orange-500' :
                  flow.anomalyScore >= 20 ? 'bg-amber-500' :
                    'bg-emerald-500',
            )}
            style={{ width: `${flow.anomalyScore}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Flow detail sheet ──────────────────────────────────────────────────────

function FlowDetailSheet({
  flow, onClose, onSelectEvent, allEvents,
}: {
  flow: DataFlowSummary | null;
  onClose: () => void;
  onSelectEvent: (e: ActivityEvent) => void;
  allEvents: ActivityEvent[];
}) {
  if (!flow) return null;

  const tier = TIER_META[flow.destination.kind];
  const sampleEvents = allEvents.filter(e => flow.sampleEventIds.includes(e.id));

  return (
    <Sheet open={!!flow} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start gap-3 mb-2">
            <Network className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <Badge variant="outline" className={cn('text-[10px]', tier.color)}>{tier.label}</Badge>
                {flow.approved ? (
                  <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                    <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Approved
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-600 border-red-500/20">
                    <XCircle className="h-2.5 w-2.5 mr-1" /> Unapproved
                  </Badge>
                )}
              </div>
              <SheetTitle className="text-sm">{flow.source.label} → {flow.destination.label}</SheetTitle>
              <SheetDescription className="text-xs mt-1 font-mono">{flow.destination.host}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          {/* Classification warning */}
          {flow.classifications.some(c => c === 'itar' || c === 'phi' || c === 'cui') && (
            <div className="rounded-md border bg-amber-500/5 border-amber-500/30 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold mb-0.5">Sensitive data in this flow</p>
                <p className="text-muted-foreground">
                  This flow transfers {flow.classifications.filter(c => c === 'itar' || c === 'phi' || c === 'cui').join(', ').toUpperCase()}.
                  Classification gate + tool sanitization (ADR-013) enforce approved transfer boundaries.
                </p>
              </div>
            </div>
          )}

          {!flow.approved && (
            <div className="rounded-md border bg-red-500/5 border-red-500/30 p-3 flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold mb-0.5">Unapproved data flow</p>
                <p className="text-muted-foreground">
                  This flow is not on the approved data-flow list. Review the sample events below, then either
                  approve the flow or block it from the gateway.
                </p>
              </div>
            </div>
          )}

          {flow.anomalyScore >= 50 && (
            <div className="rounded-md border bg-orange-500/5 border-orange-500/30 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold mb-0.5">Anomaly score: {flow.anomalyScore}/100</p>
                <p className="text-muted-foreground">
                  Flow volume or destination differs significantly from the 30-day baseline. Investigate sample events.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <MetricTile label="Events (24h)" value={flow.eventCount24h.toLocaleString()} />
            <MetricTile label="Last activity" value={relativeTime(flow.lastActivityAt)} />
            <MetricTile label="Bytes out (24h)" value={formatBytes(flow.bytesOut24h)} />
            <MetricTile label="Bytes in (24h)" value={formatBytes(flow.bytesIn24h)} />
            <MetricTile label="First seen" value={relativeTime(flow.firstSeen)} />
            <MetricTile label="Anomaly score" value={`${flow.anomalyScore} / 100`} />
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Sample events</p>
            {sampleEvents.length > 0 ? (
              <div className="rounded-md border divide-y">
                {sampleEvents.map(e => (
                  <div
                    key={e.id}
                    className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/30 cursor-pointer"
                    onClick={() => onSelectEvent(e)}
                  >
                    <span className="font-mono text-[9px] text-muted-foreground shrink-0">#{e.sequenceNumber}</span>
                    <span className="flex-1 min-w-0 truncate">{e.summary}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{relativeTime(e.timestamp)}</span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-md border p-4 text-center text-xs text-muted-foreground">
                No recent sample events captured. Full event stream available in Activity Trail.
              </div>
            )}
          </div>

          {/* Platform audit 2026-06-12: Approve/Block/Alert had NO backend — they
              toast.success'd "approved / block drafted / alert created" on a
              compliance surface while persisting nothing. Disabled with an honest
              note instead of faking enforcement. */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex flex-wrap gap-2">
              {!flow.approved && (
                <Button size="sm" disabled title="Flow policy controls are not wired to a backend yet">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve Flow
                </Button>
              )}
              <Button size="sm" variant="outline" disabled title="Flow policy controls are not wired to a backend yet">
                <Lock className="h-3.5 w-3.5 mr-1" /> Block at Gateway
              </Button>
              <Button size="sm" variant="outline" disabled title="Flow policy controls are not wired to a backend yet">
                <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Add Alert
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Flow approve / block / alert policy controls are not yet available — data-flow enforcement is observed-only today.
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n === 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
